import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Download, FileUp, RefreshCw } from 'lucide-react';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Card, CardHead, INK } from '../abf/onboarding/review/reviewStyles';
import { Note, SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import { Table, when, words } from '../abf/board/boardShared';
import { getPcDocuments, pcErrorMessage } from './api';
import type { PcDocument, PcDocuments } from './types';

/**
 * The Documents workspace of a private credit facility (PC workflow parity, slice 4): every document
 * linked to the facility with how it was read (which template, what validation said), who approved it
 * and whether it booked; the review page and the file for each. A document is evidence until its
 * booking posts.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.5;
  h2, p { margin: 0; } h2 { font-size: 16px; color: ${INK.strong}; }
  .upload { display: flex; flex-wrap: wrap; gap: 8px; }
`;

const authorityTone = (a: PcDocument['authority']): StatusTone =>
  a === 'BOOKED' ? 'done' : a === 'BOOKING_FAILED' || a === 'REJECTED' ? 'critical' : a === 'APPROVED' ? 'progress' : 'neutral';
const authorityWords: Record<PcDocument['authority'], string> = {
  BOOKED: 'booked', BOOKING_FAILED: 'booking failed', APPROVED: 'approved, not booked', REJECTED: 'rejected', READ: 'read, awaiting review',
};
const typeWords: Record<string, string> = {
  CREDIT_AGREEMENT: 'Credit agreement', LOAN_NOTICE: 'Loan notice', PAYDOWN_NOTICE: 'Paydown notice', AMENDMENT: 'Amendment', COMPLIANCE_CERTIFICATE: 'Compliance certificate',
};

const DocumentsWorkspace: React.FC<{ loanId: number; refreshKey?: number | string }> = ({ loanId, refreshKey }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<PcDocuments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await getPcDocuments(loanId)); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The documents could not be read.')); }
    finally { setBusy(false); }
  }, [loanId]);
  useEffect(() => { void load(); }, [load, refreshKey]);
  if (!data) return <Note role={error ? 'alert' : 'status'}><p>{error || "Reading the facility's documents…"}</p></Note>;

  return <Shell aria-label="Documents">
    <Card>
      <CardHead>
        <div><h2>Documents on this facility</h2><p>{data.documents.length} linked. {data.authorityBoundary}</p></div>
        <SmallButton type="button" onClick={() => void load()} disabled={busy}><RefreshCw size={14} /> Refresh</SmallButton>
      </CardHead>
      <Table style={{ margin: 0 }}>
        <thead><tr><th>Document</th><th>Type</th><th>Read with</th><th>Validation</th><th>Approved</th><th>State</th><th>When</th><th /></tr></thead>
        <tbody>
          {data.documents.map(d => <tr key={d.jobId}>
            <td><Link to={d.reviewPath}><strong>{d.documentName}</strong></Link><br /><code style={{ fontSize: 11, color: INK.faint }}>{d.jobId.slice(0, 8)}</code></td>
            <td>{typeWords[d.documentType] || words(d.documentType)}</td>
            <td>{d.templateVersion ? `template v${d.templateVersion}` : <span className="muted">built-in instructions</span>}</td>
            <td>{d.validationStatus ? <StatusChip tone={d.validationStatus === 'PASS' ? 'done' : d.validationStatus === 'FAIL' ? 'warning' : 'neutral'} label={`${words(d.validationStatus)}${d.validationErrors && d.validationErrors !== '0' ? ` · ${d.validationErrors}` : ''}`} /> : <span className="muted">—</span>}</td>
            <td>{d.reviewedBy ? <>{d.reviewedBy}<br /><small className="muted">{String(d.reviewedAt || '').slice(0, 10)}</small></> : <span className="muted">—</span>}</td>
            <td><StatusChip tone={authorityTone(d.authority)} label={authorityWords[d.authority]} />{d.lastBookingError && <><br /><small style={{ color: '#f8a5a5' }}>{d.lastBookingError}</small></>}</td>
            <td className="muted">{when(d.completedAt || d.createdAt)}</td>
            <td><a href={d.downloadPath} target="_blank" rel="noreferrer" title="Open the file" style={{ color: INK.accentSoft }}><Download size={14} /></a></td>
          </tr>)}
          {data.documents.length === 0 && <tr><td colSpan={8} className="muted">No document is linked to this facility yet.</td></tr>}
        </tbody>
      </Table>
    </Card>
    <Card>
      <CardHead><div><h2>Add a document</h2><p>Upload it in Document Processing with its type chosen; the read is reviewed there and booked against this facility by its borrower.</p></div></CardHead>
      <div className="upload">
        {data.uploadTypes.map(t => <SmallButton key={t} type="button" onClick={() => navigate(`/middle-office?tab=document-processing&documentType=${t}`)}><FileUp size={14} /> {typeWords[t] || words(t)}</SmallButton>)}
      </div>
    </Card>
  </Shell>;
};

export default DocumentsWorkspace;
